import {
  animate,
  group,
  query,
  style,
  transition,
  trigger,
} from '@angular/animations';
import { Component, inject, Inject, Input } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup } from '@angular/forms';
import {
  APP_ROUTES_CONFIG,
  ModuleType,
  TENANT_BRANDING,
  TenantBranding,
} from '@contracts';
import { finalize } from 'rxjs';
import { LoginFacade } from '../facade/login.facade';
import { buildLoginForm, buildResetForm } from '../forms/auth.forms';
import { I18nService, Lang } from '@core';

type Step = 'login' | 'reset';
type PasswordField = 'login' | 'new' | 'confirm';

type Language = {
  value: Lang;
  viewValue: string;
};

@Component({
  selector: 'feature-auth-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
  providers: [LoginFacade],
  animations: [
    trigger('formTransition', [
      transition('login => reset', [
        group([
          query(':leave', [
            animate(
              '200ms ease',
              style({ transform: 'translateX(-20px)', opacity: 0 }),
            ),
          ]),
          query(':enter', [
            style({ transform: 'translateX(20px)', opacity: 0 }),
            animate(
              '200ms ease',
              style({ transform: 'translateX(0)', opacity: 1 }),
            ),
          ]),
        ]),
      ]),
      transition('reset => login', [
        group([
          query(':leave', [
            animate(
              '200ms ease',
              style({ transform: 'translateX(20px)', opacity: 0 }),
            ),
          ]),
          query(':enter', [
            style({ transform: 'translateX(-20px)', opacity: 0 }),
            animate(
              '200ms ease',
              style({ transform: 'translateX(0)', opacity: 1 }),
            ),
          ]),
        ]),
      ]),
    ]),
  ],
})
export class LoginComponent {
  private config = inject(APP_ROUTES_CONFIG);

  @Input() onLoginSuccess?: (module: ModuleType) => void;
  @Input() onLoginError?: (error: unknown) => void;
  @Input() onResetError?: (error: unknown) => void;
  module = this.config.defaultModule ?? 'RMS';
  step: Step = 'login';

  isSubmitting = false;
  isLoginError = false;
  isResetError = false;

  private loginEmail = '';
  private loginPassword = '';

  passwordVisible: Record<PasswordField, boolean> = {
    login: false,
    new: false,
    confirm: false,
  };

  loginForm = buildLoginForm(this.fb);
  resetForm = buildResetForm(this.fb);

  languages: Language[] = [
    { value: 'en', viewValue: 'English' },
    { value: 'th', viewValue: 'ไทย' },
  ];

  selectedLanguage = this.getLang();

  constructor(
    private readonly fb: FormBuilder,
    private readonly facade: LoginFacade,
    private i18n: I18nService,
    @Inject(TENANT_BRANDING) public readonly branding: TenantBranding,
  ) {}

  private getLang(): Lang {
    return (localStorage.getItem('lang') as Lang) || 'th';
  }

  togglePassword(field: PasswordField, e: Event) {
    e.preventDefault();
    e.stopPropagation();
    this.passwordVisible[field] = !this.passwordVisible[field];
  }

  passwordInputType(field: PasswordField) {
    return this.passwordVisible[field] ? 'text' : 'password';
  }

  passwordIcon(field: PasswordField) {
    return this.passwordVisible[field] ? 'visibility' : 'visibility_off';
  }

  passwordAria(field: PasswordField) {
    return this.passwordVisible[field] ? 'Hide password' : 'Show password';
  }

  login() {
    this.isLoginError = false;

    if (this.loginForm.invalid || this.isSubmitting) {
      this.loginForm.markAllAsTouched();
      return;
    }

    const email = this.loginForm.value.email?.trim() ?? '';
    const password = this.loginForm.value.password ?? '';

    this.loginEmail = email;
    this.loginPassword = password;
    this.isSubmitting = true;

    this.facade
      .login(email, password)
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: (result) => {
          if (result.requiresPasswordSetup) {
            this.step = 'reset';
            this.resetForm.reset();
            return;
          }
          this.facade.completeLogin(result.data, this.module);
          this.onLoginSuccess?.(this.module);
        },
        error: (error) => {
          this.isLoginError = true;
          this.onLoginError?.(error);
        },
      });
  }

  submitResetPassword() {
    this.isResetError = false;

    if (this.resetForm.invalid || this.isSubmitting) {
      this.resetForm.markAllAsTouched();
      return;
    }

    const newPassword = this.resetForm.value.newPassword ?? '';
    this.isSubmitting = true;

    this.facade
      .submitResetPassword(
        this.loginEmail,
        this.loginPassword,
        newPassword,
        this.module,
      )
      .pipe(finalize(() => (this.isSubmitting = false)))
      .subscribe({
        next: () => {
          this.onLoginSuccess?.(this.module);
        },
        error: (error) => {
          this.isResetError = true;
          this.onResetError?.(error);
        },
      });
  }

  backToLogin() {
    this.step = 'login';
    this.resetForm.reset();
  }

  // onSelectLanguage(value: any) {
  //   this.i18n.switchLang(value);
  // }
  onSelectLanguage(value: Lang) {
    this.selectedLanguage = value;
    this.i18n.switchLang(value);
  }

  get selectedLanguageLabel(): string {
    return (
      this.languages.find((x) => x.value === this.selectedLanguage)
        ?.viewValue ?? ''
    );
  }

  private getControl(
    formType: 'login' | 'reset',
    controlName: string,
  ): AbstractControl | null {
    const form: FormGroup =
      formType === 'login'
        ? (this.loginForm as FormGroup)
        : (this.resetForm as FormGroup);

    return form.get(controlName);
  }

  getErrorMessage(formType: 'login' | 'reset', controlName: string): string {
    const control = this.getControl(formType, controlName);
    if (!control || !control.errors) return '';

    const e = control.errors;

    if (e['required']) return 'จำเป็นต้องกรอกข้อมูล';
    if (e['email']) return 'รูปแบบอีเมลไม่ถูกต้อง';
    if (e['minlength']) {
      return `มีตัวอักษรอย่างน้อย ${e['minlength'].requiredLength} ตัวอักษร`;
    }
    if (e['maxlength']) {
      return `ต้องไม่เกิน ${e['maxlength'].requiredLength} ตัวอักษร`;
    }
    if (e['pattern']) return 'รูปแบบข้อมูลไม่ถูกต้อง';

    return 'ข้อมูลไม่ถูกต้อง';
  }
}
